import api from "@/helpers/api";
import moment from "moment";
import { sortBy, isEmpty, omitBy } from "lodash";
import _ from "lodash";

function itemQuantity(item) {
  return Number(item && item.quantity) || 0;
}

function isCustomSeat(item) {
  return (
    item.type === "custom_seat" ||
    (item.item && item.item.webshop_custom_form === "custom_seat")
  );
}

function remillFormData(item, now) {
  const formData = item.form_data || {};
  return {
    ...formData,
    is_remill: true,
    last_reference: formData.is_remill
      ? formData.last_reference
      : item.reference,
    last_order_date: now,
    last_foam_type: formData.is_remill
      ? formData.last_foam_type
      : formData.foam_type,
    remill_webform_id: item.id,
    last_mix_back_foam_type: formData.is_remill
      ? formData.last_mix_back_foam_type
      : formData.mix_back_foam_type,
    last_mix_seat_foam_type: formData.is_remill
      ? formData.last_mix_seat_foam_type
      : formData.mix_seat_foam_type,
  };
}

function submittedWebFormInput(
  item,
  { now, isAdmin, customerAccountId, currentUserId, quantity, formData },
) {
  const input = {
    reference: item.reference,
    shipping_address_id:
      item.shipping_address_id || item.shipping_address?.id || null,
    comments: item.comments,
    quantity,
    type: item.type,
    form_data: formData,
    is_submitted: true,
    submitted_at: now,
    file_id: item.file_id,
    item_id: item.item_id,
  };

  // User-role Hasura presets user_id + customer_account_id; those columns
  // are not on web_form_insert_input, so sending them fails the mutation.
  if (isAdmin) {
    input.customer_account_id = customerAccountId;
    input.user_id = item.user_id || currentUserId;
  }

  return input;
}

async function copyWebFormFiles(sourceItem, targetId) {
  const files = sourceItem.web_form_files || [];
  if (!files.length) return;

  await Promise.all(
    files.map((f) =>
      api.webFormFiles.create({ file_id: f.file_id, web_form_id: targetId }),
    ),
  );
}

async function createCustomSeatRemills(
  item,
  { now, isAdmin, customerAccountId, currentUserId },
) {
  const extraCopies = itemQuantity(item) - 1;
  if (!isCustomSeat(item) || extraCopies < 1) return;

  for (let i = 0; i < extraCopies; i++) {
    const copy = await api.webForms.create(
      submittedWebFormInput(item, {
        now,
        isAdmin,
        customerAccountId,
        currentUserId,
        quantity: 1,
        formData: remillFormData(item, now),
      }),
    );
    await copyWebFormFiles(item, copy.id);
  }
}

export const state = {
  items: [],
};

export const getters = {
  cartCount(state) {
    return state.items.length;
  },
  cartItems(state) {
    return sortBy(state.items, (a) => a.created_at).reverse();
  },
};

export const mutations = {
  INIT(state, items) {
    state.items = [...(items || [])];
  },
  SAVE_CART(state, id) {
    if (id) {
      sessionStorage.setItem(id, JSON.stringify(state.items));
    }
  },
  CLEAR_CART(state) {
    state.items = [];
  },
  ADD_CART_ITEM(state, item) {
    state.items = [
      ...state.items,
      {
        ...item,
      },
    ];
  },
  REMOVE_CART_ITEM(state, item) {
    state.items = state.items.filter((i) => i.id !== item.id);
  },
  UPDATE_CART_ITEM(state, item) {
    state.items = state.items.map((i) => {
      if (i.id === item.id) {
        i = { ...item };
      }

      return i;
    });
  },
};

export const actions = {
  async cartSubscription({ commit, rootState, dispatch }) {
    //let userId = rootState.auth.currentUser.id
    const observer = api.webForms.webFormByUserIdSubscription();
    observer.subscribe({
      next({ data }) {
        commit("INIT", data.items);
      },
      error(error) {
        console.error("error subscription", error.message);
      },
    });
  },
  async getCart({ commit, rootState }) {
    const profile = rootState.auth.userProfile || {};
    const isAdmin = profile.role === "admin";
    const customerAccountId = profile.customer_account_id;

    const filters = [{ is_submitted: { _eq: false } }];
    if (!isAdmin) {
      if (!customerAccountId) return commit("INIT", []);
      filters.push({ customer_account_id: { _eq: customerAccountId } });
    }

    let { items } = await api.webForms.fetchPage({
      page: 1,
      pageSize: -1,
      filters,
      order_by: { created_at: "asc" },
    });
    return commit("INIT", items);
  },
  async addToCart(
    { commit, state, dispatch, rootState },
    { data, web_form_files },
  ) {
    const { allow_grouping = false, specs, web_form_files: _ignored, ...webFormData } = data;
    const isAdmin = rootState.auth.userProfile?.role === "admin";
    const customer_account_id =
      webFormData.customer_account_id ||
      rootState.auth.selectedCustomerAccountId ||
      rootState.auth.userProfile?.customer_account_id;

    if (!customer_account_id) {
      throw new Error("customer_account_id is required");
    }

    delete webFormData.customer_account_id;
    delete webFormData.user_id;

    // Custom forms always create a new cart line; never merge quantity.
    const canGroup = allow_grouping && !webFormData.type;

    let eItem = state.items.find((ci) => ci.item_id === webFormData.item_id);
    if (eItem && canGroup) {
      dispatch("updateCartItem", {
        id: eItem.id,
        data: {
          quantity: eItem.quantity + webFormData.quantity,
        },
      });
    } else {
      // Admin JWT bypasses Hasura presets, so send user_id + customer explicitly.
      // User role presets both from the JWT — they must not be in the mutation input.
      const createPayload = {
        ...webFormData,
        ...(isAdmin
          ? {
              customer_account_id,
              user_id: rootState.auth.currentUser?.id,
            }
          : {}),
      };

      if (isAdmin && !createPayload.user_id) {
        throw new Error("user_id is required");
      }

      let item = await api.webForms.create(createPayload);

      if (!!web_form_files && web_form_files.length > 0) {
        let fileUpdates = web_form_files.map((f) =>
          api.webFormFiles.create({ file_id: f.file_id, web_form_id: item.id }),
        );

        await Promise.all(fileUpdates);
      }

      commit("ADD_CART_ITEM", item);
    }
  },
  async updateCartItem({ commit, rootState }, { data, id, web_form_files }) {
    let { role = "user" } = rootState.auth.userProfile;
    /*let service = api.userCartItems
    if (role === 'admin') service = api.cartItems*/

    let item = await api.webForms.update({
      id,
      ...data,
    });

    //update webform files
    await api.webFormFiles.deleteAll({ where: { web_form_id: { _eq: id } } });

    if (!!web_form_files && web_form_files.length > 0) {
      let fileUpdates = web_form_files.map((f) =>
        api.webFormFiles.create({ file_id: f.file_id, web_form_id: id }),
      );

      await Promise.all(fileUpdates);
    }

    commit("UPDATE_CART_ITEM", item);
  },
  async removeFromCart({ commit }, { id, file_id }) {
    if (!id) return;
    let item = await api.webForms.delete({
      id,
    });
    if (file_id) await api.files.delete({ id: file_id });
    commit("REMOVE_CART_ITEM", item);
  },
  async clearCart({ commit, rootState, state }) {
    let { customer_account_id } = rootState.auth.userProfile;
    if (!customer_account_id) return;

    await api.webForms.deleteAll({
      where: { customer_account_id: { _eq: customer_account_id } },
    });
    let fileIds = state.items.filter((i) => i.file_id).map((i) => i.file_id);
    await api.files.deleteAll({
      where: { id: { _in: fileIds } },
    });
    commit("CLEAR_CART");
  },
  async checkoutItem({ commit, dispatch, rootState, state }, { item }) {
    if (!item) return;

    const profile = rootState.auth.userProfile || {};
    const isAdmin = profile.role === "admin";
    const currentUserId = rootState.auth.currentUser?.id;
    const customerAccountId = isAdmin
      ? item.customer_account_id
      : profile.customer_account_id;

    if (!customerAccountId) return;
    if (isAdmin && item.user_id !== currentUserId) return;

    var now = moment().format();

    // Custom seats with qty > 1 are split into remill copies of 1 below.
    // Other product types must keep the quantity the user set in the cart.
    await api.webForms.updateAll({
      ...(isCustomSeat(item) ? { quantity: 1 } : {}),
      is_submitted: true,
      submitted_at: now,
      where: isAdmin
        ? {
            id: { _eq: item.id },
            user_id: { _eq: currentUserId },
          }
        : {
            id: { _eq: item.id },
            customer_account_id: { _eq: customerAccountId },
          },
    });

    await createCustomSeatRemills(item, {
      now,
      isAdmin,
      customerAccountId,
      currentUserId,
    });

    if (item.file_id)
      await api.files.updateAll({
        is_temporary: false,
        where: { id: { _in: [item.file_id] } },
      });

    const webFormFiles = item.web_form_files || [];
    if (webFormFiles.length > 0) {
      let webFormFileIds = webFormFiles.map((f) => f.file_id);
      if (webFormFileIds.length > 0) {
        await api.files.updateAll({
          is_temporary: false,
          where: { id: { _in: webFormFileIds } },
        });
      }
    }

    commit("REMOVE_CART_ITEM", item);
  },
  async checkoutCart({ commit, dispatch, rootState, state }) {
    let { customer_account_id } = rootState.auth.userProfile;
    if (!customer_account_id) return;

    await dispatch("getCart");
    let { items } = state;

    var now = moment().format();
    const currentUserId = rootState.auth.currentUser?.id;
    const remillSeats = items.filter(
      (i) => itemQuantity(i) > 1 && isCustomSeat(i),
    );
    const remillIds = remillSeats.map((i) => i.id);
    const otherIds = items
      .map((i) => i.id)
      .filter((id) => !remillIds.includes(id));

    if (otherIds.length > 0) {
      await api.webForms.updateAll({
        is_submitted: true,
        submitted_at: now,
        where: {
          id: { _in: otherIds },
          customer_account_id: { _eq: customer_account_id },
        },
      });
    }

    if (remillIds.length > 0) {
      await api.webForms.updateAll({
        quantity: 1,
        is_submitted: true,
        submitted_at: now,
        where: {
          id: { _in: remillIds },
          customer_account_id: { _eq: customer_account_id },
        },
      });
    }

    for (const item of remillSeats) {
      await createCustomSeatRemills(item, {
        now,
        isAdmin: false,
        customerAccountId: customer_account_id,
        currentUserId,
      });
    }

    let file_ids = state.items.filter((i) => !!i.file_id).map((i) => i.file_id);
    if (file_ids.length > 0)
      await api.files.updateAll({
        is_temporary: false,
        where: { id: { _in: file_ids } },
      });

    let image_file_ids = (state.items || [])
      .reduce((prev, current) => [...prev, ...(current.web_form_files || [])], [])
      .map((e) => e.file_id);
    if (image_file_ids.length > 0)
      await api.files.updateAll({
        is_temporary: false,
        where: { id: { _in: image_file_ids } },
      });

    commit("CLEAR_CART");
  },
};
